declare global {
  interface Window {
    oaiq?: {
      (command: string, ...args: unknown[]): void
      /** Pre-load call queue, replayed by oaiq.min.js once it takes over. */
      q?: unknown[][]
    }
  }
}

// Ported from OpenAI's official measurement pixel base snippet
// (https://developers.openai.com/ads/measurement-pixel), typed and split out
// instead of injecting a raw eval'd snippet string.
//
// `oaiq` starts as a stub that pushes each `[command, ...args]` tuple onto
// `oaiq.q`; oaiq.min.js looks for that queue (`.q`, then `.queue`), replays it
// and replaces `window.oaiq` with the real dispatcher. Every call therefore
// goes through `window.oaiq`, never the stub captured here — after load the
// stub is detached and anything pushed onto it would be dropped.
function injectPixel(pixelId: string, debug: boolean) {
  if (window.oaiq)
    return

  const oaiq: NonNullable<Window['oaiq']> = ((...args: unknown[]) => void oaiq.q!.push(args)) as any
  oaiq.q = []
  window.oaiq = oaiq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'
  // Appended to <head> rather than inserted before the first existing <script>
  // as OpenAI's snippet does. That idiom is there to get the SDK in front of a
  // page's other blocking scripts, which is moot here — this runs after mount —
  // and it silently no-ops when the document has no script tag at all, leaving
  // the queue to fill up forever. Matches the Zboží pixel on the confirmation page.
  document.head.appendChild(script)

  // `init` is queued, not sent — the SDK runs it on replay, so the pixel id is
  // in place before any `measure` call the app makes on the same tick.
  window.oaiq('init', { pixelId, debug })
}

export default defineNuxtPlugin((nuxtApp) => {
  // `openaiPixelId` is only declared in apps that opt in (see apps/exalted.com/app/config/runtime.ts),
  // so it's untyped for every other app extending this layer — cast like the Meta Pixel plugin.
  const config = useRuntimeConfig() as any
  const pixelId: string | number | undefined = config.public?.openaiPixelId
  if (!pixelId)
    return

  nuxtApp.hook('app:mounted', () => {
    // `debug` only controls the SDK's own console logging, never whether events
    // are sent — kept to dev so deployed builds stay quiet (same as the gtm /
    // delayHydration debug flags in nuxt.config.ts).
    injectPixel(String(pixelId), import.meta.dev)
  })
})
